import { AccountType, KYCStatus } from '@/common/types'
import { H3 } from '@/common/ui/h3'
import { DASHBOARD_COPY, DIALOG_COPY } from '@/locales/en'
import { Clock9, CircleAlert, Landmark, ChevronRight } from 'lucide-react'
import { ResumeApplicationCard } from '@/common/navigation/ResumeApplicationCard'
import { NavLink } from 'react-router-dom'
import { APP_ROUTES } from '@/common/constants'
import OnboardingBankAccountAlertClickedAnalytics from '@/common/analytics/events/OnboardingBankAccountAlertClickedAnalytics'
import type { ReactNode } from 'react'

function setupTradingAccountAlert(swiftIdOnboarding?: boolean) {
    return (
        <div className="w-full self-stretch p-6 bg-stone-100 rounded-2xl outline-stone-200 outline outline-1 outline-offset-[-1px] outline-Border-Outline inline-flex justify-start items-center gap-2.5 overflow-hidden">
            <div className="flex-1 flex justify-start items-start gap-6">
                <Clock9 className="mt-1" />
                <div className="flex-1 inline-flex flex-col justify-center items-start gap-2">
                    <H3 className="justify-center text-content-primary font-['Inter'] leading-snug md:text-lg text-lg">
                        {DIALOG_COPY.noTradingAccount.title}
                    </H3>
                    <div className="self-stretch justify-start text-content-secondary text-base font-normal font-['Inter'] leading-snug">
                        {swiftIdOnboarding
                            ? DIALOG_COPY.noTradingAccount.swiftIdDescription
                            : DIALOG_COPY.noTradingAccount.description}
                    </div>
                </div>
            </div>
        </div>
    )
}

function verificationUnsuccessfulAlert() {
    return (
        <div className="w-full self-stretch p-6 bg-transparent rounded-2xl outline-red-200 outline outline-1 outline-offset-[-1px] inline-flex justify-start items-center gap-2.5 overflow-hidden">
            <div className="flex-1 flex justify-start items-start gap-6">
                <CircleAlert className="text-red-600 mt-1" />
                <div className="flex-1 inline-flex flex-col justify-center items-start gap-2">
                    <H3 className="justify-center text-red-600 font-['Inter'] leading-snug md:text-lg text-lg">
                        {DASHBOARD_COPY.alerts.verificationUnsuccessful.title}
                    </H3>
                    <div className="self-stretch justify-start text-red-600 text-base font-normal font-['Inter'] leading-snug">
                        {DASHBOARD_COPY.alerts.verificationUnsuccessful.description}
                    </div>
                </div>
            </div>
        </div>
    )
}

function noBankAccountAlert(activeAccountId: string | null) {
    return (
        <NavLink
            to={APP_ROUTES.BANK_ACCOUNT}
            onClick={() => OnboardingBankAccountAlertClickedAnalytics.track({ activeAccountId })}
            className="w-full self-stretch p-6 bg-stone-100 rounded-2xl outline-stone-200 outline outline-1 outline-offset-[-1px] outline-Border-Outline inline-flex justify-start items-center gap-2.5 overflow-hidden"
        >
            <div className="flex-1 flex justify-start items-start gap-6">
                <Landmark className="mt-1" />
                <div className="flex-1 inline-flex flex-col justify-center items-start gap-2">
                    <H3 className="justify-center text-content-primary font-['Inter'] leading-snug md:text-lg text-lg">
                        {DASHBOARD_COPY.alerts.noBankAccount.title}
                    </H3>
                    <div className="self-stretch justify-start text-content-secondary text-base font-normal font-['Inter'] leading-snug">
                        {DASHBOARD_COPY.alerts.noBankAccount.description}
                    </div>
                </div>
            </div>
            <ChevronRight className="mt-1 text-content-secondary" />
        </NavLink>
    )
}

export function OnboardingAlerts({
    accountType,
    kyc,
    isLoadingKyc,
    tradingAccountStatus,
    hasBankAccount,
    activeAccountId,
    showMobileResumeApplication,
    resumeApplicationTo,
}: {
    accountType: AccountType | undefined
    kyc: KYCStatus | undefined
    isLoadingKyc: boolean
    tradingAccountStatus: string | undefined
    hasBankAccount: boolean | undefined
    activeAccountId: string | null
    showMobileResumeApplication?: boolean
    resumeApplicationTo?: string
}) {
    if (isLoadingKyc) {
        return null
    }
    const mobileResumeCard =
        showMobileResumeApplication && resumeApplicationTo ? (
            <div className="mb-10 md:hidden">
                <ResumeApplicationCard to={resumeApplicationTo} wrapperClassName="mx-0" />
            </div>
        ) : null

    let onboardingAlert: ReactNode = null

    if (kyc && kyc.status === 'VERIFICATION_FAILED') {
        onboardingAlert = <div className="mb-10">{verificationUnsuccessfulAlert()}</div>
    } else if (kyc && kyc.canInitiateKyc === false && tradingAccountStatus !== 'APPROVED') {
        onboardingAlert = (
            <div className="mb-10">
                {setupTradingAccountAlert(
                    accountType &&
                        [AccountType.COMPANY_TRUST, AccountType.INDIVIDUAL_TRUST, AccountType.COMPANY].includes(
                            accountType,
                        ),
                )}
            </div>
        )
    } else if (hasBankAccount === false) {
        onboardingAlert = <div className="mb-10">{noBankAccountAlert(activeAccountId)}</div>
    }

    if (!mobileResumeCard && !onboardingAlert) {
        return null
    }

    return (
        <>
            {mobileResumeCard}
            {onboardingAlert}
        </>
    )
}
